import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, {
  formatApiErrorDetail,
  registerUnauthorizedHandler,
  resetUnauthorizedFlag,
  setAccessToken,
  setRefreshToken,
  clearTokens,
  getAccessToken,
  setPersistSession,
} from "./api";
import useIdleLogout from "./useIdleLogout";
import { toast } from "sonner";

const AuthContext = createContext(null);

function persistAuth(data) {
  if (data?.access_token) setAccessToken(data.access_token);
  if (data?.refresh_token) setRefreshToken(data.refresh_token);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, obj = user
  const [loading, setLoading] = useState(true);
  const handledRef = useRef(false);

  const check = useCallback(async () => {
    // If we don't have a token AND we don't have any cookies that the browser
    // is willing to send, /auth/me will 401 and we just become a guest. That
    // path is silent (no redirect, no overlay) thanks to api.js.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      handledRef.current = false;
      resetUnauthorizedFlag();
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  // Re-check auth when the tab becomes visible again OR when another tab
  // signals a change via the "storage" event. This makes state like
  // `email_verified` update without a hard refresh in scenarios like:
  //   • Coach signs up on tab A → taps email link on tab B → returns to A
  //   • Coach verifies email on their iPhone in a fresh Safari session
  //     while their laptop has the dashboard open in the background
  // The VerifyEmail success handler bumps `sf_auth_state_bump` to trigger
  // the storage event across tabs; visibilitychange covers the same-tab
  // background-then-foreground path.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && getAccessToken()) {
        check();
      }
    };
    const onStorage = (e) => {
      if (e.key === "sf_auth_state_bump" && getAccessToken()) {
        check();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("storage", onStorage);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("storage", onStorage);
    };
  }, [check]);

  // Register the global "session expired" handler exactly once.
  useEffect(() => {
    registerUnauthorizedHandler(() => {
      if (handledRef.current) return;
      handledRef.current = true;
      clearTokens();
      setUser(false);
      const here = window.location.pathname + window.location.search;
      const publicPrefixes = ["/", "/login", "/signup", "/forgot-password", "/reset-password", "/features", "/pricing", "/faq", "/invite/"];
      const isPublic = publicPrefixes.some((p) => here === p || here.startsWith(p + "/") || here.startsWith(p));
      const target = isPublic ? "/login" : `/login?next=${encodeURIComponent(here)}`;
      setTimeout(() => { window.location.replace(target); }, 0);
    });
  }, []);

  const login = async (email, password, remember = true) => {
    // Flip the storage mode BEFORE we persist the fresh token so it lands
    // in the correct storage bucket (localStorage vs sessionStorage).
    setPersistSession(!!remember);
    const { data } = await api.post("/auth/login", { email, password });
    persistAuth(data);
    setUser(data);
    handledRef.current = false;
    resetUnauthorizedFlag();
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    persistAuth(data);
    setUser(data);
    handledRef.current = false;
    resetUnauthorizedFlag();
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    clearTokens();
    setUser(false);
  };

  // Session-only idle logout: fires after 60 minutes of no activity when
  // Remember me is off. No-op when Remember me is on. Guarded inside the
  // hook so switching state cleans up correctly.
  useIdleLogout({
    isAuthenticated: !!user && user !== false,
    onIdle: async () => {
      try { await logout(); } catch { /* ignore */ }
      toast.info("Signed out due to inactivity", {
        description: "Log in again to continue.",
        duration: 6000,
      });
    },
  });

  const acceptInvite = async (payload) => {
    const { data } = await api.post("/invites/accept", payload);
    persistAuth(data);
    setUser(data);
    handledRef.current = false;
    resetUnauthorizedFlag();
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, acceptInvite, refresh: check, hasToken: !!getAccessToken() }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { formatApiErrorDetail };
